import React, {
    createContext,
    useState,
    useEffect,
    useContext,
    ReactNode,
    useCallback,
} from 'react';
import axiosApexInstance from "../auth/axiosApexInstance";
import { useUser } from "./UserContext";

export interface AppAuthKey {
    id: number;
    category: string;
    authKey: string;
    isGrantable: number;
    uiShow: number;
    uiGroup: string;
    uiSubgroup: string;
    uiTitle: string;
    uiDescription: string;
    uiDispOrder?: number;
    delFlag: number;
    configValue?: string;
}

export interface AppAuthGrant {
    id: number;
    authKey: number;
    grantedGroup: string | null;
    grantedUserId: number | null;
    grantedUserEmail: string | null;
    canGrant: number;
    delFlag: number;
}

interface AppAuthContextType {
    authKeys: AppAuthKey[];
    loggedInUserGrants: AppAuthGrant[];
    selectedUserGrants: AppAuthGrant[];
    fetchAuthKeys: () => void;
    fetchSelectedUserGrants: (userId: number) => void;
    addGrant: (grant: AppAuthGrant) => void;
    removeGrant: (id: number) => void;
    removeGrantByUserAndAuthKey: (userId: number, authKey: number) => void;
    addAuthKey: (newAuthKey: AppAuthKey) => void;
    hasAccess: (authKey: string) => boolean;
}

const AppAuthContext = createContext<AppAuthContextType | undefined>(undefined);

export const AppAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [authKeys, setAuthKeys] = useState<AppAuthKey[]>([]);
    const [loggedInUserGrants, setLoggedInUserGrants] = useState<AppAuthGrant[]>([]);
    const [selectedUserGrants, setSelectedUserGrants] = useState<AppAuthGrant[]>([]);
    const { apexUser, user } = useUser();

    const fetchAuthKeys = useCallback(async () => {
        try {
            const response = await axiosApexInstance.get('/task/authkeys/keys');
            setAuthKeys(response.data);
        } catch (error) {
            console.error('Error fetching auth keys:', error);
        }
    }, []);

    const fetchLoggedInUserGrants = useCallback(async (userId: number) => {
        try {
            const response = await axiosApexInstance.get(`/task/authkeys/grant/${userId}`);
            setLoggedInUserGrants(response.data);
        } catch (error) {
            console.error('Error fetching logged-in user grants:', error);
        }
    }, []);

    useEffect(() => {
        if (user?.uid && apexUser?.userId) {
            fetchAuthKeys();
            fetchLoggedInUserGrants(apexUser.userId);
        }
    }, [user, apexUser, fetchAuthKeys, fetchLoggedInUserGrants]);

    const fetchSelectedUserGrants = useCallback(async (userId: number) => {
        try {
            const response = await axiosApexInstance.get(`/task/authkeys/grant/${userId}`);
            //console.log("User grants for selected used", userId, response.data);
            setSelectedUserGrants(response.data);
        } catch (error) {
            console.error('Error fetching selected user grants:', error);
        }
    }, []);

    const addGrant = async (grant: AppAuthGrant) => {
        try {
            await axiosApexInstance.post('/task/authkeys/grant/add', grant);
            setSelectedUserGrants(prev => [...prev, grant]);
        } catch (error) {
            console.error('Error adding grant:', error);
        }
    };

    const removeGrant = async (id: number) => {
        try {
            await axiosApexInstance.delete(`/task/authkeys/grant/${id}`);
            setSelectedUserGrants(prev => prev.filter(grant => grant.id !== id));
        } catch (error) {
            console.error('Error removing grant:', error);
        }
    };

    const removeGrantByUserAndAuthKey = async (userId: number, authKey: number) => {
        try {
            await axiosApexInstance.delete(`/task/authkeys/grant/user/${userId}/authkey/${authKey}`);
            // Refresh the grants to get updated state
            await fetchSelectedUserGrants(userId);
        } catch (error) {
            console.error('Error removing grant by user and authkey:', error);
        }
    };

    const addAuthKey = async (newAuthKey: AppAuthKey) => {
        try {
            await axiosApexInstance.post('/task/authkeys/add', newAuthKey);
            fetchAuthKeys(); // Reload to get updated ID
        } catch (error) {
            console.error('Error adding auth key:', error);
        }
    };

    const hasAccess = useCallback((authKeyStr: string): boolean => {
        const key = authKeys.find(k => k.authKey === authKeyStr);
        if (!key) {
            // Debug logging removed for production
            // console.log(`hasAccess('${authKeyStr}'): Auth key not found in authKeys array`);
            return false;
        }
        const hasGrant = loggedInUserGrants.some(g => g.authKey === key.id && g.delFlag === 0);
        if (!hasGrant) {
            // Debug logging removed for production
            // console.log(`hasAccess('${authKeyStr}'): No grant found. Key ID: ${key.id}, Grants:`, loggedInUserGrants.map(g => ({ authKey: g.authKey, delFlag: g.delFlag })));
        }
        return hasGrant;
    }, [authKeys, loggedInUserGrants]);

            return (
            <AppAuthContext.Provider
                value={{
                    authKeys,
                    loggedInUserGrants,
                    selectedUserGrants,
                    fetchAuthKeys,
                    fetchSelectedUserGrants,
                    addGrant,
                    removeGrant,
                    removeGrantByUserAndAuthKey,
                    addAuthKey,
                    hasAccess
                }}
            >
                {children}
            </AppAuthContext.Provider>
        );
};

export const useAppAuth = (): AppAuthContextType => {
    const context = useContext(AppAuthContext);
    if (!context) {
        throw new Error('useAppAuth must be used within an AppAuthProvider');
    }
    return context;
};
